import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from "classnames";
import { useComputed, useSignal } from "@preact/signals";
import { percentileInRange } from "../../../lib/array";
import { getThresholdForValue, getThresholdsColorForValue } from "../../../lib/thresholds";
import Table from "../../Table";
import Button from "../../Button";
import tableStyle from "../../Table/style.module.scss";
import { formatNumber } from "../../../lib/number";
import Icon from "../../Icon";
import Tooltip from "../../Tooltip";
import HeatMapTooltip from "../../Tooltip/HeatMapTooltip";
import { isDebugMode } from "../../../lib/utils";
import style from "./style.module.scss";
const DEFAULT_LOW_SAMPLE_ABS = 100;
const DEFAULT_LOW_SAMPLE_PER = 2;
function getPercentileColorForValue(percentile) {
    let color = "var(--medium-emphasis)";
    switch (percentile) {
        case 0:
            color = "#a1dab4";
            break;
        case 1:
            color = "#8ed3b4";
            break;
        case 2:
            color = "#7bccb5";
            break;
        case 3:
            color = "#68c5b6";
            break;
        case 4:
            color = "#55beb8";
            break;
        case 5:
            color = "#41b6ba";
            break;
        case 6:
            color = "#2caebc";
            break;
        case 7:
            color = "#13a5bd";
            break;
        case 8:
            color = "#009cbe";
            break;
        case 9:
            color = "#0093bd";
            break;
        case 10:
            color = "#008abb";
            break;
    }
    return color;
}
export default function Heatmap(props) {
    var _a;
    const { dimensionColumnHeadings, metricHeading, enableThresholds = false, fullMetricNames = false, sampleSizeFromFirstSeries = false, lowSampleRateWarnings = false, showFirstRows = 10, transformers = [], cellActionURL, } = props;
    const cellWrapper = props.cellWrapper || ((children) => children);
    const showAllMetrics = useSignal(false);
    const maxRows = useComputed(() => (showAllMetrics.value ? Infinity : showFirstRows));
    const isCompareMode = props.data.find((series) => {
        return !!series.isCompareSeries;
    });
    const dataSeries = (() => {
        // Apply all the transformers from left to right
        const data = transformers.reduce((result, transformer) => transformer(result), props.data);
        if (sampleSizeFromFirstSeries && data[0].metricQualified.slice(0, 3) === "LUX") {
            // Create a new sample size series based on the first series
            const sampleSizeSeries = Object.assign(Object.assign({}, data[0]), { isSampleSize: true, suffix: "" });
            //If in compare mode we need to run this again with the second compare set of data.
            if (isCompareMode !== undefined) {
                const sampleSizeSeriesCompare = Object.assign(Object.assign({}, isCompareMode), { isSampleSize: true, suffix: "" });
                const nonCompareData = data.filter((series) => {
                    return series.isCompareSeries === undefined;
                });
                const compareData = data.filter((series) => {
                    return series.isCompareSeries === true;
                });
                return [sampleSizeSeries, ...nonCompareData, sampleSizeSeriesCompare, ...compareData];
            }
            else {
                return [sampleSizeSeries, ...data];
            }
        }
        return data;
    })();
    const columnHeadings = [];
    const headingClassNames = [
        style["column-heading"],
        {
            // Widen the heading column when there are a small number of series
            [style["column-heading--wide"]]: dataSeries.length < 4,
            [style["column-heading--wider"]]: dataSeries.length < 3,
        },
    ];
    if (dimensionColumnHeadings) {
        columnHeadings.push(...dimensionColumnHeadings.map((heading) => (_jsx("th", { className: classNames(...headingClassNames, style[`column-heading--${dimensionColumnHeadings.length}`]), children: heading }, heading))));
    }
    else {
        columnHeadings.push(_jsx("th", { className: classNames(...headingClassNames), children: metricHeading }));
    }
    columnHeadings.push(...dataSeries.map((series) => {
        let prefix = "";
        if (isCompareMode) {
            if (series.isCompareSeries) {
                prefix = "Previous ";
            }
            else {
                prefix = "Latest ";
            }
        }
        if (isSampleSizeSeries(series)) {
            return (_jsxs("th", { className: classNames(style["cell--right-align"], style["cell--percentage"]), children: [prefix, "% of page views"] }, series.metricName));
        }
        else {
            return (_jsxs("th", { className: style["cell--center-align"], children: [prefix, fullMetricNames ? series.name : series.metricShortName] }, series.metricName));
        }
    }));
    const queryDialect = ((_a = dataSeries[0]) === null || _a === void 0 ? void 0 : _a.queryDialect) || "default";
    return (_jsxs(Table, { className: classNames(style["table"], "qa-heatmap", { [`query-dialect-${queryDialect}`]: isDebugMode() }), highlightRowOnHover: true, children: [_jsx("thead", { children: _jsx("tr", { children: columnHeadings }) }), _jsxs("tbody", { children: [_jsx(HeatmapRows, { data: dataSeries, maxRows: maxRows.value, enableThresholds: enableThresholds, lowSampleRateWarnings: lowSampleRateWarnings, cellWrapper: cellWrapper, cellActionURL: cellActionURL }), dataSeries[0].data.length > showFirstRows && (_jsx(MoreOrLessButton, { colSpan: dataSeries.length + 1, onClick: (showAll) => (showAllMetrics.value = showAll), expanded: showAllMetrics.value }))] })] }));
}
function isSampleSizeSeries(series) {
    return series.metric === "pvs" || series.isSampleSize === true;
}
function HeatmapRows(props) {
    const { data, enableThresholds, maxRows = Infinity, cellWrapper, cellActionURL, lowSampleRateWarnings } = props;
    const rowData = getRowData(data).slice(0, maxRows);
    const rows = [];
    // Convert rowData from an array of rows to an array of columns
    const dataByColumn = [];
    rowData.forEach((row) => {
        row.forEach((cell, idx) => {
            if (!dataByColumn[idx]) {
                dataByColumn[idx] = [];
            }
            dataByColumn[idx].push(cell);
        });
    });
    function onClick(event, cell, color, lowSampleRate, label, cellActionURL) {
        event.preventDefault();
        event.stopPropagation();
        Tooltip.showForMouseEvent(event, () => getTooltip(cell, color, lowSampleRate, label, cellActionURL));
    }
    function getTooltip(cell, color, lowSampleRate, label, cellActionURL) {
        return (_jsx(HeatMapTooltip, { cell: cell, label: label, color: color, lowSampleRate: lowSampleRate, cellActionURL: cellActionURL }));
    }
    rowData.forEach((columns) => {
        const cells = [];
        const label = columns.find((cell) => "label" in cell);
        let rowPVCount = null;
        columns.forEach((cell, colIdx) => {
            if ("label" in cell) {
                // First column is always a label
                cells.push(_jsx("td", { className: style["cell--label"], children: cellWrapper(cell.label, cell, label) }, cell.label));
            }
            else {
                const { value, series } = cell;
                const isPercentageValue = typeof value === "string" && value.slice(-1) === "%";
                if (isPercentageValue) {
                    if (cell.num) {
                        //This is our guideline for total traffic for the row.
                        rowPVCount = cell.num;
                    }
                    // Percentage values are formatted differently
                    cells.push(_jsx("td", { className: classNames(style["cell--right-align"], style["cell--percentage"]), children: value }));
                }
                else if (typeof value === "string" && value !== "-") {
                    // Cells with no value have no formatting
                    cells.push(_jsx("td", { className: classNames(style["cell--no-data"]), children: value }));
                }
                else {
                    // Regular numeric metric values
                    const cellClasses = [style["cell--center-align"]];
                    const suffix = cell.series.suffix || "";
                    let lowSampleRate = false;
                    let color = "#FFFFFF";
                    if (typeof value === "number") {
                        if (enableThresholds && series.thresholds) {
                            // Use thresholds where available to determine cell colour
                            const threshold = getThresholdForValue(series.thresholds, value);
                            color = getThresholdsColorForValue(series.thresholds, value);
                            cellClasses.push(style["threshold"], style[`threshold--${threshold.type}`]);
                        }
                        else {
                            // Otherwise set the colour based on the percentile of the value. The
                            // percentile is rounded to the previous 10th.
                            const columnData = dataByColumn[colIdx];
                            const percentile = Math.floor(percentileInRange(value, columnData.map((d) => d.value)) / 10);
                            color = getPercentileColorForValue(percentile);
                            cellClasses.push(style["percentile"], style[`percentile--${percentile}`]);
                        }
                        if (lowSampleRateWarnings && cell.num) {
                            //Check if cell is a low percentage of traffic compared to the total traffic for the row in the chart.
                            let cellPer = 0;
                            if (rowPVCount !== null && cell.num) {
                                cellPer = (cell.num / rowPVCount) * 100;
                            }
                            if ((cell.num && cell.num <= DEFAULT_LOW_SAMPLE_ABS) ||
                                (cellPer && cellPer <= DEFAULT_LOW_SAMPLE_PER)) {
                                lowSampleRate = true;
                                //Cell has a low sample rate.
                                cellClasses.push(style["low-sample-rate"]);
                                color = "var(--medium-emphasis)";
                            }
                        }
                    }
                    else if (value === "-") {
                        cellClasses.push(style["percentile"], style["percentile--null"]);
                        color = "var(--medium-emphasis)";
                    }
                    cells.push(_jsx("td", { className: classNames(cellClasses, style["heat-cell"]), onClick: (event) => {
                            if (cellActionURL) {
                                onClick(event, cell, color, lowSampleRate, label, cellActionURL(cell, label));
                            }
                            else {
                                onClick(event, cell, color, lowSampleRate, label);
                            }
                        }, children: cellWrapper(_jsxs(_Fragment, { children: [typeof value === "number" ? formatNumber(value) : value, value === "-" ? "" : suffix, lowSampleRate ? _jsx(Icon, { name: "help" }) : null] }), cell, label) }));
                }
            }
        });
        rows.push(_jsx("tr", { children: cells }));
    });
    return rows;
}
function MoreOrLessButton(props) {
    const showMoreOrLessButton = props.expanded ? (_jsx(Button, { variation: "secondary", onClick: () => props.onClick(false), children: "Show Less" })) : (_jsx(Button, { variation: "secondary", onClick: () => props.onClick(true), children: "Show More" }));
    if (showMoreOrLessButton) {
        return (_jsx("tr", { className: tableStyle["no-highlight"], children: _jsx("td", { colSpan: props.colSpan, className: tableStyle["table__cell--overflow-button-col"], children: _jsx("div", { className: tableStyle["table__cell--overflow-button"], children: showMoreOrLessButton }) }) }));
    }
    return null;
}
function getRowData(series) {
    const key = (point) => point.item;
    const sortCol = "num";
    // Build a map of row headings, using the sample size series if available
    const sampleSizeSeries = series.find(isSampleSizeSeries);
    const rowHeadingItems = new Map(sampleSizeSeries === null || sampleSizeSeries === void 0 ? void 0 : sampleSizeSeries.data.map((point) => [key(point), point]));
    // Use the data series to fill in any gaps from the sample size series
    const dataSeries = series.filter((s) => !isSampleSizeSeries(s));
    dataSeries.forEach((series) => {
        series.data.forEach((point) => {
            if (!rowHeadingItems.has(key(point))) {
                // When we have a sample size series, missing items are given a sort value of 0 so
                // that they are sorted to the end of the list.
                const sortVal = sampleSizeSeries ? 0 : point[sortCol];
                rowHeadingItems.set(key(point), Object.assign(Object.assign({}, point), { suffix: series.suffix, [sortCol]: sortVal }));
            }
            else if (!sampleSizeSeries && rowHeadingItems.get(key(point))[sortCol] > point[sortCol]) {
                // When we don't have a sample size series, pick the item with the highest sort value
                rowHeadingItems.set(key(point), Object.assign(Object.assign({}, point), { suffix: series.suffix }));
            }
        });
    });
    // Sort the items by their count to determine the row order
    const rowHeadings = Array.from(rowHeadingItems.values()).sort((a, b) => b[sortCol] - a[sortCol]);
    // Build a row for each heading
    return rowHeadings.map((point) => {
        const rowData = [];
        if (point.items) {
            rowData.push(...point.items.map((label) => ({ label })));
        }
        else {
            rowData.push({ label: point.item });
        }
        series.forEach((series) => {
            const matchingDatum = series.data.find((d) => d.item === point.item);
            const valueRow = {
                series,
                value: "-",
            };
            if (isSampleSizeSeries(series)) {
                if (!matchingDatum) {
                    // The item doesn't exist in this series, so it had no page views
                    valueRow.value = "0%";
                    valueRow.num = 0;
                }
                else if (matchingDatum.per === 0) {
                    // Percentages are rounded to 1 decimal place, so zero is actually <0.1%
                    valueRow.value = "< 0.1%";
                    valueRow.num = matchingDatum.num;
                }
                else {
                    valueRow.value = `${matchingDatum.per}%`;
                    valueRow.num = matchingDatum.num;
                }
            }
            else if (matchingDatum && matchingDatum.y !== null) {
                valueRow.value = matchingDatum.y;
                if (matchingDatum.num !== null) {
                    valueRow.num = matchingDatum.num;
                }
                if (matchingDatum.per !== null) {
                    valueRow.per = matchingDatum.per;
                }
            }
            rowData.push(valueRow);
        });
        return rowData;
    });
}
